import { useState } from 'react';
import { Search, MapPin, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useSearchPublicPlaces, type PublicPlaceResult } from '@/hooks/useSearchPublicPlaces';
import { cn } from '@/lib/utils';
import type { QualifySelectedPlace } from '@/lib/producer-qualify';

interface Props {
  value: QualifySelectedPlace | null;
  onChange: (place: QualifySelectedPlace | null) => void;
  onMissingPlace: () => void;
}

export function QualifyStepPlaceSelect({ value, onChange, onMissingPlace }: Props) {
  const [searchQuery, setSearchQuery] = useState('');
  const { data: results = [], isLoading } = useSearchPublicPlaces(searchQuery);
  const safeValue = value?.id ? value : null;

  if (safeValue) {
    return (
      <div className="space-y-6">
        <h2 className="font-display text-xl text-foreground">Vilken plats hör du till?</h2>

        {/* Selected place preview */}
        <div className="rounded-xl border border-primary/20 bg-primary/5 p-5 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1 min-w-0">
              <p className="font-medium text-foreground">{safeValue.name}</p>
              {(safeValue.municipality || safeValue.landskap) && (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5 shrink-0" />
                  {[safeValue.municipality, safeValue.landskap].filter(Boolean).join(' · ')}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => onChange(null)}
              className="text-muted-foreground hover:text-foreground transition-colors p-1 -m-1"
              aria-label="Ändra val"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {safeValue.description_short && (
            <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
              {safeValue.description_short}
            </p>
          )}

          <p className="text-xs text-muted-foreground/70">
            Det här är platsen din verksamhet kopplas till i Farmly.
          </p>
        </div>
      </div>
    );
  }

  const handleSelectPlace = (place: PublicPlaceResult) => {
    const id = place.id?.trim();

    if (!id) {
      if (import.meta.env.DEV) {
        console.warn('QUALIFY DEBUG: place result missing id', place);
      }
      return;
    }

    onChange({
      id,
      name: place.name,
      municipality: place.municipality,
      landskap: place.landskap,
      description_short: place.description_short,
    });
  };

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <h2 className="font-display text-xl text-foreground">Vilken plats hör du till?</h2>
        <p className="text-sm text-muted-foreground">
          Sök efter platsen eller området där din verksamhet finns.
        </p>
      </div>

      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Sök plats eller område…"
          className="pl-9"
          autoFocus
        />
      </div>

      {/* Results */}
      {searchQuery.trim().length >= 2 && (
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {isLoading ? (
            <p className="text-sm text-muted-foreground py-3 text-center">Söker…</p>
          ) : results.length === 0 ? (
            <p className="text-sm text-muted-foreground py-3 text-center">
              Inga platser hittades.
            </p>
          ) : (
            results.map((place) => (
              <PlaceResultRow
                key={place.id}
                place={place}
                onSelect={() => handleSelectPlace(place)}
              />
            ))
          )}
        </div>
      )}

      {/* "Can't find my place" */}
      <button
        type="button"
        onClick={onMissingPlace}
        className="text-sm text-primary hover:text-primary/80 transition-colors underline underline-offset-2"
      >
        Jag hittar inte min plats
      </button>
    </div>
  );
}

function PlaceResultRow({
  place,
  onSelect,
}: {
  place: PublicPlaceResult;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'w-full text-left px-3 py-2.5 rounded-lg',
        'hover:bg-accent/50 transition-colors',
        'flex items-start gap-3'
      )}
    >
      <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{place.name}</p>
        {(place.municipality || place.landskap) && (
          <p className="text-xs text-muted-foreground truncate">
            {[place.municipality, place.landskap].filter(Boolean).join(' · ')}
          </p>
        )}
      </div>
    </button>
  );
}
